/**
 * lens-popup-flow.js
 * Custom element <lens-popup-flow> — popup-based lens selection for non-VTO products.
 * Steps: 0 (Add Lenses intro) → 1 (Power Types) → 2 (Add-Ons) → 3 (Prescription)
 */

if (!customElements.get('lens-popup-flow')) {
  class LensPopupFlow extends HTMLElement {
    constructor() {
      super();

      /* ── State ─────────────────────────────────────────── */
      this.currentStep        = 0;
      this.selectedLensVariantId   = null;
      this.selectedLensPrice       = 0;       // numeric, paise/cents
      this.selectedLensPriceStr    = '';      // formatted string e.g. "₹2,899.00"
      this.selectedLensTitle       = '';
      this.selectedAddons          = [];      // [{variantId, price, priceStr, title}]
      this.selectedLensBtn         = null;    // reference to the active lens variant button
      this.frameVariantId          = this.dataset.productId;
      this.basePriceRaw            = parseFloat(this.dataset.productPriceRaw || 0);
      this.uploadedFile            = null;
      this.currencySymbol          = '₹';
      this.isCustomProduct         = this.dataset.isCustomProduct === 'true';
    }

    /* ══════════════════════════════════════════════════════
       LIFECYCLE
    ══════════════════════════════════════════════════════ */
    connectedCallback() {
      this._bindElements();
      this._bindEvents();
    }

    /* ══════════════════════════════════════════════════════
       ELEMENT REFERENCES
    ══════════════════════════════════════════════════════ */
    _bindElements() {
      // Overlay
      this.overlay = this.querySelector('.lpf-overlay');

      // Steps
      this.steps = this.querySelectorAll('.lpf-step');

      // Step 0
      this.buyWithLensBtn  = this.querySelector('.lpf-buy-with-lens-btn');
      this.frameOnlyBtn    = this.querySelector('.lpf-frame-only-btn');
      this.frameSubmitBtn  = this.querySelector('.lpf-hidden-frame-submit');
      this.frameForm       = this.querySelector('.' + 'lpf-frame-variant-id')?.closest('form');

      // Step 1 – tabs + variant cards
      this.tabs         = this.querySelectorAll('.lpf-tab');
      this.variantCards = this.querySelectorAll('.lpf-variant-card');
      this.variantAddBtns = this.querySelectorAll('.lpf-variant-add-btn');

      // Step 2 – addons
      this.addonCards    = this.querySelectorAll('.lpf-addon-card');
      this.addonAddBtns  = this.querySelectorAll('.lpf-addon-add-btn');
      this.noAddonsEl    = this.querySelector('.lpf-no-addons');
      this.skipBtn       = this.querySelector('.lpf-skip-btn');
      this.continueWrap  = this.querySelector('.lpf-continue-wrap');
      this.continueBtn   = this.querySelector('.lpf-continue-btn');

      // Step 3 – prescription
      this.proceedBtn      = this.querySelector('.lpf-proceed-btn');
      this.fileInput       = this.querySelector('.lpf-file-input');
      this.prescFileInput  = this.querySelector('.lpf-prescription-file-input');
      this.uploadBox       = this.querySelector('.lpf-upload-box');
      this.uploadPreview   = this.querySelector('.lpf-upload-preview');
      this.uploadPreviewName = this.querySelector('.lpf-upload-preview__name');
      this.uploadRemoveBtn = this.querySelector('.lpf-upload-preview__remove');

      // Lens hidden form
      this.lensVariantInput      = this.querySelector('.lpf-lens-variant-input');
      this.lensMainProductInput  = this.querySelector('.lpf-main-product-id-input');
      this.lensTimestampInput    = this.querySelector('.lpf-timestamp-input');
      this.lensAddonDetailsInput = this.querySelector('.lpf-addon-details-input');
      this.lensAddonValidInput   = this.querySelector('.lpf-addon-validation-input');
      this.lensSubmitBtn         = this.querySelector('.lpf-hidden-lens-submit');
      this.lensProductForm       = this.lensSubmitBtn?.closest('product-form');

      // Subtotal displays (Steps 2 & 3)
      this.subtotalDisplays = this.querySelectorAll('.lpf-subtotal-display');
    }

    /* ══════════════════════════════════════════════════════
       EVENT BINDING
    ══════════════════════════════════════════════════════ */
    _bindEvents() {
      // Overlay click → close
      this.overlay?.addEventListener('click', (e) => {
        if (e.target === this.overlay) this.close();
      });

      // Delegate clicks for close and back buttons to ensure they always fire
      this.addEventListener('click', (e) => {
        const closeBtn = e.target.closest('.lpf-close-btn');
        if (closeBtn) {
          e.preventDefault();
          e.stopPropagation();
          this.close();
          return;
        }

        const backBtn = e.target.closest('.lpf-back-btn');
        if (backBtn) {
          e.preventDefault();
          e.stopPropagation();
          this._goBack();
          return;
        }
      });

      /* Step 0 */
      this.buyWithLensBtn?.addEventListener('click', () => this._showStep(1));

      this.frameOnlyBtn?.addEventListener('click', () => this._addFrameToCart());

      /* Continue Button for Banner Flow */
      this.continueBtn?.addEventListener('click', () => {
        if (this.selectedLensVariantId) {
          this._showStep(2);
        }
      });

      /* Step 1 — Tabs */
      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          this.tabs.forEach(t => t.classList.remove('active'));
          tab.classList.add('active');
          this._filterVariants(tab.dataset.category);
        });
      });

      /* Step 1 — Variant Add buttons */
      this.variantAddBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          if (btn.disabled) return;

          const newVariantId = btn.dataset.variantId;

          // Only reset addons if a DIFFERENT lens is selected
          if (newVariantId !== this.selectedLensVariantId) {
            if (this.selectedLensBtn && !this.selectedLensBtn.disabled) {
              this.selectedLensBtn.textContent = 'Add';
              this.selectedLensBtn.classList.remove('added');
            }
            this.selectedAddons = [];
            this._resetAddonButtons();
          }

          this.selectedLensVariantId = newVariantId;
          this.selectedLensPriceStr  = btn.dataset.variantPrice;
          this.selectedLensPrice     = parseFloat(btn.dataset.variantPriceRaw || 0);
          this.selectedLensTitle     = btn.dataset.variantTitle;
          this.selectedLensBtn       = btn;

          // Mark button as selected
          btn.textContent = 'Added';
          btn.classList.add('added');

          this._updateSubtotals();
          this._filterAddons(this.selectedLensVariantId);
          if (!this.isBannerFlow || e.isTrusted) {
            this._showStep(2);
          }
        });
      });

      /* Step 2 — Addon Add/Remove buttons */
      this.addonAddBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          if (btn.disabled) return;
          const variantId  = btn.dataset.addonVariantId;
          const priceRaw   = parseFloat(btn.dataset.addonPriceRaw || 0);
          const priceStr   = btn.dataset.addonPrice;
          const title      = btn.dataset.addonTitle;

          const existingIdx = this.selectedAddons.findIndex(a => a.variantId === variantId);

          if (existingIdx > -1) {
            // Deselect
            this.selectedAddons.splice(existingIdx, 1);
            btn.classList.remove('added');
            btn.textContent = 'Add';
          } else {
            // Select
            this.selectedAddons.push({ variantId, price: priceRaw, priceStr, title });
            btn.classList.add('added');
            btn.textContent = 'Added';

            // Auto advance or submit
            setTimeout(() => {
              if (this.isCustomProduct) {
                this._showStep(3);
              } else {
                this._submitLensFlow();
              }
            }, 300);
          }

          this._updateSubtotals();
        });
      });

      /* Step 2 — Skip & Continue */
      this.skipBtn?.addEventListener('click', () => {
        if (this.isCustomProduct) {
          this._showStep(3);
        } else {
          this._submitLensFlow();
        }
      });

      /* Step 3 — File input */
      this.fileInput?.addEventListener('click', (e) => { e.target.value = ''; });
      this.fileInput?.addEventListener('change', () => {
        if (this.fileInput.files.length) {
          this._handleFileSelect(this.fileInput.files[0]);
        }
      });

      /* Step 3 — Remove file */
      this.uploadRemoveBtn?.addEventListener('click', () => this._clearFile());

      /* Step 3 — Proceed */
      this.proceedBtn?.addEventListener('click', () => this._submitLensFlow());
    }

    /* --------------------------------------------------------------------------
       PUBLIC API
    -------------------------------------------------------------------------- */
    open(preselectVariant = null) {
      this._reset();
      this.classList.add('active');
      document.body.style.overflow = 'hidden';
      this.isBannerFlow = false;
      if (this.continueWrap) this.continueWrap.style.display = 'none';

      if (this.isCustomProduct) {
        this._showStep(3);
      } else {
        if (preselectVariant) {
          this._showStep(1);
        } else {
          this._showStep(0);
        }
      }

      // Handle preselection
      if (preselectVariant) {
        this.isBannerFlow = true;
        if (this.continueWrap) this.continueWrap.style.display = 'block';
        setTimeout(() => {
          const variantCardToPreselect = Array.from(this.variantCards).find(card => {
            const titleEl = card.querySelector('.lpf-variant-card__name');
            return titleEl && titleEl.textContent.trim().toLowerCase() === preselectVariant.toLowerCase();
          });

          if (variantCardToPreselect && !variantCardToPreselect.classList.contains('lpf-soldout')) {
            // 1. Find the category of this variant
            const category = variantCardToPreselect.dataset.category;
            if (category) {
              // 2. Select this category tab in step 1
              const categoryTab = Array.from(this.tabs).find(tab => tab.dataset.category === category);
              if (categoryTab) {
                categoryTab.click();
                
                // 3. Click the variant card's select button itself
                setTimeout(() => {
                  const selectBtn = variantCardToPreselect.querySelector('.lpf-variant-add-btn');
                  if (selectBtn) {
                    selectBtn.click();
                  }
                }, 50);
              }
            }
          }
        }, 50);
      }
    }

    close() {
      this.classList.remove('active');
      document.body.style.overflow = '';
    }

    /* ══════════════════════════════════════════════════════
       STEP NAVIGATION
    ══════════════════════════════════════════════════════ */
    _showStep(index) {
      this.currentStep = index;
      this.steps.forEach(step => step.classList.remove('active'));
      const target = this.querySelector(`[data-step="${index}"]`);
      if (target) target.classList.add('active');
    }

    _goBack() {
      if (this.currentStep === 3) {
        if (this.isCustomProduct) {
          this.close();
        } else {
          this._showStep(2);
        }
      } else if (this.currentStep === 2) {
        // Going back to lens selection — KEEP selections intact
        this._showStep(1);
      } else if (this.currentStep === 1) {
        this._showStep(0);
      } else {
        this.close();
      }
    }

    /* ══════════════════════════════════════════════════════
       FILTERING
    ══════════════════════════════════════════════════════ */
    _filterVariants(category) {
      this.variantCards.forEach(card => {
        if (card.dataset.category === category) {
          card.classList.remove('hidden');
        } else {
          card.classList.add('hidden');
        }
      });
    }

    _filterAddons(lensVariantId) {
      let hasAddons = false;
      this.addonCards.forEach(card => {
        if (card.dataset.lensVariantId === lensVariantId) {
          card.classList.remove('hidden');
          hasAddons = true;
        } else {
          card.classList.add('hidden');
        }
      });

      if (this.noAddonsEl) {
        this.noAddonsEl.classList.toggle('hidden', hasAddons);
      }
    }

    /* ══════════════════════════════════════════════════════
       SUBTOTAL
    ══════════════════════════════════════════════════════ */
    _updateSubtotals() {
      const addonTotal = this.selectedAddons.reduce((sum, a) => sum + a.price, 0);
      // Subtotal = selected lens + addons only (frame price is NOT included)
      const total = this.selectedLensPrice + addonTotal;

      const sym = this.currencySymbol || '\u20b9';
      const formatted = sym + total.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      this.subtotalDisplays.forEach(el => {
        el.textContent = formatted;
      });
    }

    /* ══════════════════════════════════════════════════════
       FILE UPLOAD
    ══════════════════════════════════════════════════════ */
    _handleFileSelect(file) {
      const allowed = ['jpg', 'jpeg', 'png', 'pdf'];
      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowed.includes(ext)) {
        alert('Please upload a JPG, JPEG, PNG, or PDF file.');
        return;
      }
      this.uploadedFile = file;

      if (this.uploadBox)    this.uploadBox.classList.add('hidden');
      if (this.uploadPreview) {
        this.uploadPreview.classList.remove('hidden');
        if (this.uploadPreviewName) this.uploadPreviewName.textContent = file.name;
      }

      // Mirror to hidden form file input
      if (this.prescFileInput) {
        const dt = new DataTransfer();
        dt.items.add(file);
        this.prescFileInput.files = dt.files;
      }
    }

    _clearFile() {
      this.uploadedFile = null;
      if (this.fileInput)    this.fileInput.value = '';
      if (this.prescFileInput) this.prescFileInput.value = '';
      if (this.uploadBox)    this.uploadBox.classList.remove('hidden');
      if (this.uploadPreview) this.uploadPreview.classList.add('hidden');
      if (this.uploadPreviewName) this.uploadPreviewName.textContent = '';
    }

    /* ══════════════════════════════════════════════════════
       RESET
    ══════════════════════════════════════════════════════ */
    _reset() {
      this.selectedLensVariantId = null;
      this.selectedLensPrice     = 0;
      this.selectedLensPriceStr  = '';
      this.selectedLensTitle     = '';
      this.selectedAddons        = [];

      // Restore selected lens button to default state
      if (this.selectedLensBtn && !this.selectedLensBtn.disabled) {
        this.selectedLensBtn.textContent = 'Add';
        this.selectedLensBtn.classList.remove('added');
      }
      this.selectedLensBtn = null;

      this._clearFile();
      this._resetAddonButtons();

      // Reset tab to first
      this.tabs.forEach((tab, i) => tab.classList.toggle('active', i === 0));
      if (this.tabs.length > 0) this._filterVariants(this.tabs[0]?.dataset.category);

      // Reset subtotal to ₹0 for normal flow (lens+addons), or frame price for custom-product
      if (this.isCustomProduct) {
        const framePriceStr = this.dataset.productPrice || '';
        this.subtotalDisplays.forEach(el => { el.textContent = framePriceStr; });
      } else {
        const sym = this.currencySymbol || '\u20b9';
        this.subtotalDisplays.forEach(el => { el.textContent = sym + '0.00'; });
      }
    }

    _resetAddonButtons() {
      this.addonAddBtns.forEach(btn => {
        btn.classList.remove('added');
        if (!btn.disabled) btn.textContent = 'Add';
      });
    }

    /* ══════════════════════════════════════════════════════
       TIMESTAMP
    ══════════════════════════════════════════════════════ */
    _getTimestamp() {
      const addonsStr = this.selectedAddons.map(a => a.variantId).join('|');
      const rxStr = this.uploadedFile ? this.uploadedFile.name : '';
      const str = `${addonsStr}-${rxStr}`;
      
      let hash = 0;
      for (let i = 0, len = str.length; i < len; i++) {
          let chr = str.charCodeAt(i);
          hash = (hash << 5) - hash + chr;
          hash |= 0;
      }
      return 'config_' + Math.abs(hash).toString(16);
    }

    /* ══════════════════════════════════════════════════════
       CART SUBMISSION
    ══════════════════════════════════════════════════════ */

    /** Frame-only ATC (Step 0 — "I need Only Frames") */
    _addFrameToCart() {
      if (this.frameSubmitBtn) {
        // Ensure the AB cart property rides along on the frame form
        const frameForm = this.frameSubmitBtn.closest('form');
        if (frameForm && !frameForm.querySelector('input[name="properties[_ab_cart]"]')) {
          const abInput = document.createElement('input');
          abInput.type  = 'hidden';
          abInput.name  = 'properties[_ab_cart]';
          abInput.value = 'AB cart';
          frameForm.appendChild(abInput);
        }
        this.frameOnlyBtn.disabled = true;
        this.frameOnlyBtn.textContent = 'Adding…';
        this.frameSubmitBtn.click();
        // The product-form custom element handles the rest; close popup on success
        // Listen for cart update pubsub event
        const unsubscribe = (window.subscribe && window.PUB_SUB_EVENTS)
          ? window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
              this.close();
              unsubscribe();
            })
          : null;
        // Fallback close after 3s
        setTimeout(() => {
          this.close();
          if (unsubscribe) unsubscribe();
        }, 3000);
      }
    }

    /** Full lens + addons + prescription flow (Step 3 — Proceed) */
    async _submitLensFlow() {
      if (!this.isCustomProduct && !this.selectedLensVariantId) {
        alert('Please select a lens first.');
        return;
      }

      this.proceedBtn.disabled = true;
      this.proceedBtn.classList.add('loading');

      const timestamp = this._getTimestamp();

      // 1. Close popup immediately and show global spinner
      this.close();
      const loadingEffectEl = document.querySelector(".loading-overlay-effect");
      if (loadingEffectEl) {
        loadingEffectEl.classList.add("active");
        document.body.classList.add("overflow-hidden");
      }

      try {
        const formData = new FormData();

        if (this.isCustomProduct) {
          // Custom Product: Just submit the frame and the prescription file
          formData.append('id', this.frameVariantId);
          formData.append('quantity', '1');
        } else {
          // Normal Flow: Submit the selected lens + link it to the frame
          formData.append('id', this.selectedLensVariantId);
          formData.append('quantity', '1');
          formData.append('properties[main-product-id]', this.selectedLensVariantId);
          formData.append('properties[timestamp]', timestamp);
        }

        // Tag orders coming through the lens popup  ✅ formData now exists
        formData.append('properties[_ab_cart]', 'AB cart');

        const hasAddons = this.selectedAddons.length > 0;
        if (hasAddons) {
          const addonDetails = this.selectedAddons.map(a => `${a.title}||${a.priceStr}`).join(', ');
          formData.append('properties[add-ons]', addonDetails);
          formData.append('properties[add-on-validation]', 'true');
        }

        if (this.uploadedFile) {
          formData.append('properties[prescription]', this.uploadedFile);
        }

        // 3. Submit main lens
        const lensRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        if (!lensRes.ok) {
          throw new Error('Failed to add lens to cart.');
        }

        // 4. Submit all addons (if any)
        if (hasAddons) {
          await this._addExtraAddons(this.selectedAddons, this.selectedLensVariantId, timestamp);
        }

        // 5. Fetch fresh cart sections directly
        const sectionRes = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`);
        const sectionData = await sectionRes.json();

        // Remove loading state once network is complete
        if (loadingEffectEl) {
          loadingEffectEl.classList.remove("active");
          document.body.classList.remove("overflow-hidden");
        }

        // 6. Update Cart Drawer UI
        this._updateCartDrawer({ sections: sectionData });
        
        // Always attempt to open the drawer
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          // If theme relies on 'is-empty' class, remove it
          cartDrawer.classList.remove('is-empty');
          cartDrawer.open();
        }

      } catch (err) {
        console.error('[LensPopupFlow] Submission error:', err);
        alert('Something went wrong adding items to cart. Please try again.');
        
        // Remove loading state on error
        if (loadingEffectEl) {
          loadingEffectEl.classList.remove("active");
          document.body.classList.remove("overflow-hidden");
        }
        
        // Re-open popup so they can try again
        this.open();
        this._showStep(3);
      } finally {
        this.proceedBtn.disabled = false;
        this.proceedBtn.classList.remove('loading');
      }
    }

    _updateCartDrawer(parsedState) {
      if (!parsedState || !parsedState.sections) return false;
      
      const sectionsToRender = [
          { id: 'shopify-section-cart-drawer', htmlKey: 'cart-drawer' },
          { id: 'cart-icon-bubble', htmlKey: 'cart-icon-bubble' }
      ];
      
      let allRendered = true;
      sectionsToRender.forEach(section => {
          const sectionHtml = parsedState.sections[section.htmlKey];
          const sectionElement = document.querySelector(`#${section.id}`);
          
          if (sectionHtml && sectionElement) {
              const parsedDom = new DOMParser().parseFromString(sectionHtml, 'text/html');
              const innerElement = parsedDom.querySelector(`#${section.id}`) || parsedDom.querySelector('.shopify-section');
              if (innerElement) {
                  sectionElement.innerHTML = innerElement.innerHTML;
              } else {
                  sectionElement.innerHTML = sectionHtml;
              }
          } else {
              allRendered = false;
          }
      });
      return allRendered;
    }

    /** Add extra addons (beyond first) via /cart/add.js JSON API */
    async _addExtraAddons(addons, lensVariantId, timestamp) {
      const items = addons.map(addon => ({
        id: parseInt(addon.variantId),
        quantity: 1,
        properties: {
          'add-on-product': true,
          'main-product-id': lensVariantId,
          'timestamp': timestamp
        }
      }));

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        throw new Error(`Failed to add addon: ${response.statusText}`);
      }
      return response.json();
    }
  }

  customElements.define('lens-popup-flow', LensPopupFlow);
}

/* ══════════════════════════════════════════════════════════════
   Trigger element — <popup-atc-trigger>
   Wraps the "Add to Cart" button and opens the popup on click.
══════════════════════════════════════════════════════════════ */
if (!customElements.get('popup-atc-trigger')) {
  class PopupAtcTrigger extends HTMLElement {
    connectedCallback() {
      this.atcBtn = this.querySelector('.lpf-atc-trigger-btn');
      this.atcBtn?.addEventListener('click', () => this._openPopup());
    }

    _openPopup() {
      const popup = document.querySelector('lens-popup-flow');
      if (!popup) return;
      const preselectVariant = this.atcBtn?.dataset.preselectVariant || null;
      popup.open(preselectVariant);
    }
  }

  customElements.define('popup-atc-trigger', PopupAtcTrigger);
}
